import { useState, useEffect, useCallback } from 'react';
import { NotificationSoundPreferences, notificationSettingsApi } from '../services/notificationSettingsApi';
import { useAuth } from './useAuth';

interface UseSoundPreferencesReturn {
  preferences: NotificationSoundPreferences | null;
  loading: boolean;
  error: string | null;
  updatePreferences: (updates: Partial<NotificationSoundPreferences>) => Promise<void>;
  playNotificationSound: (
    urgencyLevel: 'routine' | 'urgent' | 'critical' | 'ai' | 'system' | 'reply',
    options?: { force?: boolean }
  ) => void;
  refreshPreferences: () => Promise<void>;
}

// Default preferences fallback - Use fixed sounds as specified
const DEFAULT_PREFERENCES: NotificationSoundPreferences = {
  user: 0,
  enable_sound_alerts: true,
  master_volume: 0.8,
  sound_theme: 'default',
  
  routine_sound: 'routine-fixed.wav',      // Soft Marimba 0.2s
  routine_volume: 0.5,
  urgent_sound: 'urgent-fixed.wav',        // Bright Chime 0.4s  
  urgent_volume: 0.7,
  critical_sound: 'critical-fixed.wav',    // Strong Bell 0.5s
  critical_volume: 0.9,
  ai_sound: 'ai-fixed.wav',               // Soft Digital 0.2s
  ai_volume: 0.6,
  system_sound: 'system-fixed.wav',       // Professional Chime 0.3s
  system_volume: 0.6,
  reply_sound: 'reply-fixed.wav',         // Soft Click 0.15s
  reply_volume: 0.4,
  
  respect_quiet_hours: true,
  emergency_override_quiet_hours: true,
  enable_vibration: true,
  vibration_intensity: 'medium'
};

export const useSoundPreferences = (): UseSoundPreferencesReturn => {
  const [preferences, setPreferences] = useState<NotificationSoundPreferences | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, validationState } = useAuth();

  // Load preferences on mount
  const refreshPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Only fetch preferences if user is authenticated AND validation is complete
      if (!isAuthenticated || validationState !== 'valid') {
        if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
          console.log('[SoundPrefs] User not authenticated, using defaults');
        }
        setPreferences(DEFAULT_PREFERENCES);
        setLoading(false);
        return;
      }
      
      const prefs = await notificationSettingsApi.sounds.getPreferences();
      setPreferences(prefs);
    } catch (err: any) {
      // Suppress 401 errors during initial load (expected when not authenticated)
      if (err?.response?.status === 401 && validationState !== 'valid') {
        if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
          console.log('[SoundPrefs] Expected 401, using defaults');
        }
        setPreferences(DEFAULT_PREFERENCES);
        setLoading(false);
        return;
      }
      
      console.error('Failed to load sound preferences:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sound preferences');
      
      // Use default preferences as fallback
      setPreferences(DEFAULT_PREFERENCES);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, validationState]);

  useEffect(() => {
    refreshPreferences();
  }, [refreshPreferences]);

  // Update preferences
  const updatePreferences = useCallback(async (updates: Partial<NotificationSoundPreferences>) => {
    if (!preferences) return;

    // Only update preferences if user is authenticated
    if (!isAuthenticated) {
      console.warn('Cannot update sound preferences: user not authenticated');
      return;
    }

    const updatedPrefs = { ...preferences, ...updates };
    
    try {
      setError(null);
      const response = await notificationSettingsApi.sounds.updatePreferences(updates);
      
      if (response.data) {
        setPreferences(response.data);
      } else {
        setPreferences(updatedPrefs); // Optimistic update
      }
    } catch (err) {
      console.error('Failed to update sound preferences:', err);
      setError(err instanceof Error ? err.message : 'Failed to update sound preferences');
      
      // Revert optimistic update
      throw err;
    }
  }, [preferences, isAuthenticated]);

  // Check if it's quiet hours
  const isQuietHours = useCallback((): boolean => {
    if (!preferences || !preferences.respect_quiet_hours) return false;
    
    // This would need integration with the existing quiet hours system
    // For now, return false - the actual implementation would check
    // the user's quiet hours settings from NotificationPreference model
    return false;
  }, [preferences]);

  // Play notification sound based on user preferences
  const playNotificationSound = useCallback((
    urgencyLevel: 'routine' | 'urgent' | 'critical' | 'ai' | 'system' | 'reply',
    options?: { force?: boolean }
  ) => {
    const prefs = preferences || DEFAULT_PREFERENCES;
    const force = options?.force === true;
    
    // Check if sounds are enabled
    if (!prefs.enable_sound_alerts && !force) {
      return;
    }

    // Check quiet hours (allow critical sounds if emergency override is enabled)
    if (
      !force &&
      isQuietHours() &&
      !(urgencyLevel === 'critical' && prefs.emergency_override_quiet_hours)
    ) {
      return;
    }

    try {
      // Get sound file and volume for this urgency level
      const soundMapping: { [key: string]: { file: keyof NotificationSoundPreferences, volume: keyof NotificationSoundPreferences } } = {
        routine: { file: 'routine_sound', volume: 'routine_volume' },
        urgent: { file: 'urgent_sound', volume: 'urgent_volume' },
        critical: { file: 'critical_sound', volume: 'critical_volume' },
        ai: { file: 'ai_sound', volume: 'ai_volume' },
        system: { file: 'system_sound', volume: 'system_volume' },
        reply: { file: 'reply_sound', volume: 'reply_volume' }
      };

      const mapping = soundMapping[urgencyLevel];
      if (!mapping) {
        console.warn(`Unknown urgency level: ${urgencyLevel}`);
        return;
      }

      const soundFile = prefs[mapping.file] as string;
      const individualVolume = prefs[mapping.volume] as number;
      const finalVolume = Math.min(1.0, individualVolume * prefs.master_volume);

      // Create and play audio
      const audio = new Audio(`/sounds/${soundFile}`);
      audio.volume = finalVolume;

      audio.play().catch((playError) => {
        console.warn(`Could not play sound: ${soundFile}`, playError);
        
        // Try fallback to default sound
        if (soundFile !== 'notification.wav') {
          const fallbackAudio = new Audio('/sounds/notification.wav');
          fallbackAudio.volume = finalVolume;
          fallbackAudio.play().catch((fallbackError) => {
            console.warn('Fallback sound also failed:', fallbackError);
          });
        }
      });

      // Handle vibration
      if (prefs.enable_vibration && 'vibrate' in navigator) {
        const vibrationPatterns = {
          light: {
            critical: [200, 100, 200, 100, 200],
            urgent: [150, 50, 150],
            routine: [100],
            ai: [50, 25, 50],
            system: [125],
            reply: [75]
          },
          medium: {
            critical: [300, 150, 300, 150, 300],
            urgent: [250, 75, 250],
            routine: [150],
            ai: [75, 50, 75],
            system: [200],
            reply: [100]
          },
          strong: {
            critical: [500, 200, 500, 200, 500],
            urgent: [400, 100, 400],
            routine: [250],
            ai: [125, 75, 125],
            system: [300],
            reply: [150]
          }
        };

        const intensityPatterns = vibrationPatterns[prefs.vibration_intensity];
        const pattern = intensityPatterns[urgencyLevel];
        
        if (pattern) {
          navigator.vibrate(pattern);
        }
      }

    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  }, [preferences, isQuietHours]);

  return {
    preferences,
    loading,
    error,
    updatePreferences,
    playNotificationSound,
    refreshPreferences
  };
};

export default useSoundPreferences;
